import React, { useEffect, useState } from "react";
import { Profile } from "../../../../interfaces/Profile";
import { PessoaVinculoDetalhe } from "../../../../interfaces/Identidade";

interface Props {
  vinculo: PessoaVinculoDetalhe;
  perfisAtivos: Profile[];
  busy: boolean;
  onToggleAtivo: (v: PessoaVinculoDetalhe) => void;
  onSalvarPerfis: (v: PessoaVinculoDetalhe, ids: number[]) => Promise<void>;
}

/**
 * Um vínculo (pessoa x organização) com os perfis dele. Seleção múltipla em checkboxes, igual à
 * tabela de perfis por usuário; aqui há botão salvar porque o PUT substitui o conjunto inteiro.
 */
const VinculoPerfis: React.FC<Props> = ({ vinculo, perfisAtivos, busy, onToggleAtivo, onSalvarPerfis }) => {
  const original = vinculo.perfis.map((p) => p.id);
  const [ids, setIds] = useState<number[]>(original);

  useEffect(() => {
    setIds(vinculo.perfis.map((p) => p.id));
  }, [vinculo]);

  const sujo = ids.length !== original.length || ids.some((id) => !original.includes(id));

  const toggle = (id: number) =>
    setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <li className="rounded-2xl border border-gray-100 p-4 dark:border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="truncate text-sm font-bold text-navy-700 dark:text-white"
            title={vinculo.cliente}
          >
            {vinculo.cliente}
          </span>
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              vinculo.ativo
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300"
            }`}
          >
            {vinculo.ativo ? "Ativo" : "Desativado"}
          </span>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => onToggleAtivo(vinculo)}
          className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
        >
          {vinculo.ativo ? "Desativar" : "Reativar"}
        </button>
      </div>

      <fieldset className="mt-3" disabled={busy}>
        <legend className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
          Perfis nesta organização
        </legend>
        {perfisAtivos.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Nenhum perfil ativo para escolher. Crie um em Perfis de acesso.
          </p>
        ) : (
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {perfisAtivos.map((p) => {
              const inputId = `vinculo-${vinculo.vinculoId}-perfil-${p.id}`;
              return (
                <label
                  key={p.id}
                  htmlFor={inputId}
                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5"
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={ids.includes(p.id)}
                    onChange={() => toggle(p.id)}
                    className="h-4 w-4 accent-brand-500"
                  />
                  <span className="truncate" title={p.name}>
                    {p.name}
                  </span>
                </label>
              );
            })}
          </div>
        )}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={busy || !sujo}
            onClick={() => onSalvarPerfis(vinculo, ids)}
            className="rounded-xl bg-ink-900 px-4 py-2 text-xs font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            Salvar perfis
          </button>
        </div>
      </fieldset>
    </li>
  );
};

export default VinculoPerfis;
